import { useState, useEffect, useCallback, useMemo } from "react";
import { UseFaceRecognitionProps, UseFaceRecognitionReturn } from "./props";
import { useSystemConfiguration } from "../theme/useSystemConfiguration";

export const useFaceRecognition = ({
  iframeUrl,
}: UseFaceRecognitionProps): UseFaceRecognitionReturn => {
  const [url, setUrl] = useState<string | null>(iframeUrl ?? null);
  const [error, setError] = useState(false);
  const [retryUrl, setRetryUrl] = useState<string | null>(null);
  const { setting } = useSystemConfiguration();

  const domains = useMemo(() => {
    if (
      !setting ||
      setting?.origin?.faceRecognitionExternalLinks?.length === 0
    ) {
      return [];
    }

    const domainLinks = setting?.origin?.faceRecognitionExternalLinks?.map(
      (link) => link.url?.replace("https://", ""),
    );
    return domainLinks;
  }, [setting]);

  const currentDomainIndex = useMemo(() => {
    if (typeof window == "undefined") {
      return 0;
    }

    const index = domains?.indexOf(window.location.host);
    return index;
  }, [domains]);

  const generateRetryUrl = useCallback(
    (currentUrl: string) => {
      try {
        const urlObject = new URL(window.location.href);
        const nextIndex = (currentDomainIndex + 1) % domains.length;

        const searchParams = `?url=${currentUrl}`;

        urlObject.host = domains[nextIndex];
        urlObject.search = searchParams;

        const retryUrl = urlObject.toString();
        return retryUrl;
      } catch (e) {
        return null;
      }
    },
    [currentDomainIndex, domains],
  );

  const requestCameraAccess = useCallback(async () => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setError(false);
    } catch (err) {
      setError(true);

      if (url) {
        const newRetryUrl = generateRetryUrl(url);
        setRetryUrl(newRetryUrl);
      }
    }
  }, [url, generateRetryUrl]);

  useEffect(() => {
    if (!iframeUrl || error || typeof window === "undefined") {
      return;
    }
    setUrl(iframeUrl);
    requestCameraAccess();
  }, [iframeUrl, requestCameraAccess, error]);

  return {
    url,
    error,
    retryUrl,
    requestCameraAccess,
  };
};
